'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pencil, Save, X, Loader2 } from 'lucide-react'
import { apiClient } from '@/lib/api/client'
import type { Customer as HelmCustomer, CustomerType as HelmCustomerType } from '@/lib/api/types'

interface CustomerEditFormProps {
  customer: HelmCustomer
  canEdit?: boolean
  onSaved?: (customer: HelmCustomer) => void
}

export function CustomerEditForm({ customer, canEdit = false, onSaved }: CustomerEditFormProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState(customer.name)
  const [type, setType] = useState<HelmCustomerType>(customer.type)
  const [companyName, setCompanyName] = useState(customer.company_name || '')
  const [contactPerson, setContactPerson] = useState(customer.contact_person || '')
  const [tinNumber, setTinNumber] = useState(customer.tin_number)
  const [phone, setPhone] = useState(customer.phone)
  const [email, setEmail] = useState(customer.email)
  const [whatsappGroupId, setWhatsappGroupId] = useState(customer.whatsapp_group_id || '')

  function resetForm() {
    setName(customer.name)
    setType(customer.type)
    setCompanyName(customer.company_name || '')
    setContactPerson(customer.contact_person || '')
    setTinNumber(customer.tin_number)
    setPhone(customer.phone)
    setEmail(customer.email)
    setWhatsappGroupId(customer.whatsapp_group_id || '')
    setError(null)
  }

  function handleCancel() {
    resetForm()
    setIsEditing(false)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    // Client-side validation
    if (!name.trim()) {
      setError('Name is required')
      setIsSubmitting(false)
      return
    }
    if (!tinNumber.trim()) {
      setError('TIN number is required')
      setIsSubmitting(false)
      return
    }
    if (!phone.trim()) {
      setError('Phone is required')
      setIsSubmitting(false)
      return
    }
    if (!email.trim()) {
      setError('Email is required')
      setIsSubmitting(false)
      return
    }
    if (type === 'BUSINESS' && !companyName.trim()) {
      setError('Company name is required for business customers')
      setIsSubmitting(false)
      return
    }

    try {
      const updatedCustomer = await apiClient.updateCustomer(customer.id, {
        name: name.trim(),
        type,
        company_name: companyName.trim() || null,
        contact_person: contactPerson.trim() || null,
        tin_number: tinNumber.trim(),
        phone: phone.trim(),
        email: email.trim(),
        whatsapp_group_id: whatsappGroupId.trim() || null,
      })

      setIsEditing(false)
      if (onSaved) {
        onSaved(updatedCustomer)
      } else {
        window.location.reload()
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred while saving')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Display mode
  if (!isEditing) {
    return (
      <div className="space-y-6">
        {canEdit && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <Label className="text-muted-foreground text-xs uppercase">Name</Label>
            <p className="mt-1 font-medium">{customer.name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs uppercase">Type</Label>
            <p className="mt-1 font-medium">{customer.type}</p>
          </div>
          {customer.type === 'BUSINESS' && (
            <>
              <div>
                <Label className="text-muted-foreground text-xs uppercase">Company Name</Label>
                <p className="mt-1 font-medium">{customer.company_name || '-'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs uppercase">Contact Person</Label>
                <p className="mt-1 font-medium">{customer.contact_person || '-'}</p>
              </div>
            </>
          )}
          <div>
            <Label className="text-muted-foreground text-xs uppercase">TIN Number</Label>
            <p className="mt-1 font-mono">{customer.tin_number}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs uppercase">Phone</Label>
            <p className="mt-1">{customer.phone}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs uppercase">Email</Label>
            <p className="mt-1">{customer.email}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs uppercase">WhatsApp Group ID</Label>
            <p className="mt-1 font-mono text-sm">{customer.whatsapp_group_id || '-'}</p>
          </div>
        </div>

        <div className="border-t pt-4 text-sm text-muted-foreground">
          <p>Created: {new Date(customer.created_at).toLocaleDateString()}</p>
          <p>Last updated: {new Date(customer.updated_at).toLocaleDateString()}</p>
        </div>
      </div>
    )
  }

  // Edit mode
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isSubmitting}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="type">Customer Type *</Label>
          <Select value={type} onValueChange={(value: HelmCustomerType) => setType(value)} disabled={isSubmitting}>
            <SelectTrigger id="type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INDIVIDUAL">Individual</SelectItem>
              <SelectItem value="BUSINESS">Business</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {type === 'BUSINESS' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </>
        )}

        <div className="space-y-2">
          <Label htmlFor="tin_number">TIN Number *</Label>
          <Input
            id="tin_number"
            value={tinNumber}
            onChange={(e) => setTinNumber(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="whatsapp_group_id">WhatsApp Group ID</Label>
          <Input
            id="whatsapp_group_id"
            value={whatsappGroupId}
            onChange={(e) => setWhatsappGroupId(e.target.value)}
            disabled={isSubmitting}
            placeholder="Optional"
          />
        </div>
      </div>
    </form>
  )
}
